/** Formatters for dense technical data. Pure; `now` is always passed in (no hidden clock). */

const BYTE_UNITS = ["B", "KB", "MB", "GB", "TB", "PB"] as const;

/** 1536 → "1.5 KB" (binary steps, short labels). Non-finite or negative → "—". */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes) || bytes < 0) return "—";
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < BYTE_UNITS.length - 1) {
    v /= 1024;
    i++;
  }
  const digits = i === 0 || v >= 100 ? 0 : v >= 10 ? 1 : 1;
  return `${v.toFixed(digits).replace(/\.0$/, "")} ${BYTE_UNITS[i]}`;
}

/** Seconds → the two largest units: 93784 → "1 d 2 h", 59 → "59 s". */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds) || seconds < 0) return "—";
  const s = Math.floor(seconds);
  const parts: [number, string][] = [
    [Math.floor(s / 86400), "d"],
    [Math.floor((s % 86400) / 3600), "h"],
    [Math.floor((s % 3600) / 60), "min"],
    [s % 60, "s"],
  ];
  const first = parts.findIndex(([n]) => n > 0);
  if (first === -1) return "0 s";
  return parts
    .slice(first, first + 2)
    .filter(([n]) => n > 0)
    .map(([n, u]) => `${n} ${u}`)
    .join(" ");
}

function toMs(at: Date | string | number): number {
  return at instanceof Date ? at.getTime() : typeof at === "number" ? at : Date.parse(at);
}

/** "just now", "3 min ago", "2 h ago", "4 days ago", "in 5 min". Invalid → "—". */
export function formatRelativeTime(at: Date | string | number | null | undefined, now: number): string {
  if (at === null || at === undefined) return "—";
  const t = toMs(at);
  if (!Number.isFinite(t)) return "—";
  const diff = Math.round((now - t) / 1000);
  const abs = Math.abs(diff);
  const say = (n: number, unit: string) => (diff >= 0 ? `${n} ${unit} ago` : `in ${n} ${unit}`);
  if (abs < 45) return "just now";
  if (abs < 3600) return say(Math.max(1, Math.round(abs / 60)), "min");
  if (abs < 86400) return say(Math.round(abs / 3600), "h");
  const days = Math.round(abs / 86400);
  if (days < 45) return say(days, days === 1 ? "day" : "days");
  const months = Math.round(days / 30);
  if (months < 18) return say(months, months === 1 ? "month" : "months");
  const years = Math.round(days / 365);
  return say(years, years === 1 ? "year" : "years");
}

/** Absolute local timestamp for titles/tooltips. */
export function formatTimestamp(at: Date | string | number | null | undefined): string {
  if (at === null || at === undefined) return "—";
  const t = toMs(at);
  return Number.isFinite(t) ? new Date(t).toLocaleString() : "—";
}
